import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Trash2, Camera, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATING_CATEGORIES = [
  { key: 'office_timings',   label: 'Office Timings & Policy Adherence', short: 'Office · Policy' },
  { key: 'task_performance', label: 'Task Performance', short: 'Tasks' },
  { key: 'presentation',     label: 'Presentation & Group Activities', short: 'Presentation' },
  { key: 'quarterly_tests',  label: 'Quarterly Tests', short: 'Tests' },
  { key: 'proactive_client', label: 'Proactive-Initiative & Client Satisfaction', short: 'Client / Init.' },
];

// "Q3 2025-26" — quarter helper (Indian fiscal year Apr–Mar)
const currentQuarterLabel = () => {
  const d = new Date();
  const m = d.getMonth() + 1; // 1..12
  const y = d.getFullYear();
  // Q1 Apr-Jun (4-6), Q2 Jul-Sep (7-9), Q3 Oct-Dec (10-12), Q4 Jan-Mar (1-3)
  let q, fyStart;
  if (m >= 4 && m <= 6)      { q = 'Q1'; fyStart = y; }
  else if (m >= 7 && m <= 9) { q = 'Q2'; fyStart = y; }
  else if (m >= 10)          { q = 'Q3'; fyStart = y; }
  else                       { q = 'Q4'; fyStart = y - 1; }
  const fyEnd = String((fyStart + 1) % 100).padStart(2, '0');
  return `${q} ${fyStart}-${fyEnd}`;
};

// Sort chronologically by parsing "Qn YYYY-YY"
const quarterSortKey = (label) => {
  const m = (label || '').match(/^Q([1-4])\s+(\d{4})-(\d{2})$/);
  if (!m) return 0;
  const qi = parseInt(m[1], 10);
  const fyStart = parseInt(m[2], 10);
  return fyStart * 10 + qi;
};

const avg = (r) => {
  const vals = RATING_CATEGORIES.map((c) => Number(r?.[c.key] || 0)).filter((v) => v > 0);
  if (!vals.length) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

const RatingsTimeline = ({ employeeId, currentRatings = {}, onCurrentUpdated }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [quarter, setQuarter] = useState(currentQuarterLabel());

  const load = async () => {
    if (!employeeId) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/employees/${employeeId}/ratings`);
      setSnapshots(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load history');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const snapshot = async () => {
    if (!employeeId) return;
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/ratings`, {
        quarter,
        ratings: currentRatings || {},
        notes: '',
      });
      await load();
      onCurrentUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Snapshot failed');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this quarter snapshot?')) return;
    try {
      await axios.delete(`${API}/admin/ratings/${id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const sorted = useMemo(() =>
    [...snapshots].sort((a, b) => quarterSortKey(a.quarter) - quarterSortKey(b.quarter)),
    [snapshots]
  );

  // Group snapshots by FY ("Q3 2025-26" → "2025-26") and build the 4-column matrix.
  const fyGroups = useMemo(() => {
    const map = new Map();
    for (const s of sorted) {
      const m = (s.quarter || '').match(/^Q([1-4])\s+(\d{4}-\d{2})$/);
      if (!m) continue;
      const qi = parseInt(m[1], 10);
      const fy = m[2];
      if (!map.has(fy)) map.set(fy, { fy, quarters: { 1: null, 2: null, 3: null, 4: null } });
      map.get(fy).quarters[qi] = s;
    }
    // Sort FYs by starting year (newest first)
    return Array.from(map.values()).sort((a, b) => parseInt(b.fy.slice(0, 4), 10) - parseInt(a.fy.slice(0, 4), 10));
  }, [sorted]);

  const avgTrend = useMemo(() =>
    sorted.map((s) => ({ quarter: s.quarter, avg: avg(s.ratings) })),
    [sorted]
  );
  const maxTrend = 5;

  return (
    <div className="border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-gray-500 uppercase">Quarterly History</div>
          <div className="text-sm text-gray-700">{snapshots.length} snapshot{snapshots.length === 1 ? '' : 's'} on record</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={quarter}
            onChange={(e) => setQuarter(e.target.value)}
            className="border border-gray-300 px-2 py-1.5 text-xs font-mono w-28"
            placeholder="Q3 2025-26"
            data-testid="ratings-quarter-input"
          />
          <Button
            type="button"
            onClick={snapshot}
            disabled={saving}
            className="rounded-none bg-gray-900 hover:bg-black text-white h-8 text-xs px-3"
            data-testid="ratings-snapshot-btn"
          >
            {saving ? (
              <><Loader2 size={12} className="mr-1.5 animate-spin" /> Saving…</>
            ) : (
              <><Camera size={12} className="mr-1.5" /> Snapshot to Quarter</>
            )}
          </Button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">{String(error)}</div>
      )}

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
      ) : sorted.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-gray-500">
          No snapshots yet. Set the ratings above, pick a quarter and click <b>Snapshot to Quarter</b> to start the timeline.
        </div>
      ) : (
        <>
          {/* Sparkline of overall average per quarter */}
          <div className="px-4 pt-3 pb-1">
            <div className="flex items-end gap-2 h-16">
              {avgTrend.map((p, i) => {
                const h = (p.avg / maxTrend) * 100;
                const prev = i > 0 ? avgTrend[i - 1].avg : p.avg;
                const rising = p.avg > prev, falling = p.avg < prev;
                return (
                  <div key={p.quarter} className="flex-1 flex flex-col items-center justify-end min-w-0">
                    <div className="text-[10px] font-mono text-gray-500 tabular-nums mb-1">
                      {p.avg ? p.avg.toFixed(1) : '—'}
                    </div>
                    <div
                      className={`w-full ${rising ? 'bg-green-500' : falling ? 'bg-red-400' : 'bg-brand-blue'} transition-all`}
                      style={{ height: `${Math.max(h, 4)}%` }}
                      title={`${p.quarter} · ${p.avg.toFixed(1)} / 5`}
                    />
                    <div className="text-[9px] font-mono text-gray-500 mt-1 tracking-tight truncate w-full text-center">
                      {p.quarter}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Table view — 4 quarter columns per FY */}
          {fyGroups.map((grp) => {
            const filledQuarters = [1, 2, 3, 4].filter((q) => grp.quarters[q]);
            const latestQ = Math.max(...filledQuarters);
            // Overall average per quarter (for footer)
            const qAvg = { 1: 0, 2: 0, 3: 0, 4: 0 };
            [1, 2, 3, 4].forEach((q) => {
              const snap = grp.quarters[q];
              qAvg[q] = snap ? avg(snap.ratings) : 0;
            });

            return (
              <div key={grp.fy} className="overflow-x-auto border-t border-gray-200">
                <div className="px-4 py-2 bg-gray-900 text-white flex items-center justify-between">
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase">FY {grp.fy}</div>
                  <div className="text-[10px] font-mono text-white/50">
                    {filledQuarters.length}/4 quarters recorded
                  </div>
                </div>
                <table className="w-full text-xs table-fixed">
                  <colgroup>
                    <col style={{ width: '32%' }} />
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '17%' }} />
                  </colgroup>
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 border-b border-gray-200">Category</th>
                      {[1, 2, 3, 4].map((q) => {
                        const filled = !!grp.quarters[q];
                        const isLatest = filled && q === latestQ;
                        return (
                          <th
                            key={q}
                            className={`text-center px-2 py-2 border-b border-gray-200 border-l ${
                              isLatest ? 'bg-amber-50/70 text-amber-800' : ''
                            }`}
                          >
                            <div>Q{q}</div>
                            {isLatest && <div className="text-[8px] mt-0.5 text-amber-700">LATEST</div>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {RATING_CATEGORIES.map((cat, idx) => (
                      <tr key={cat.key} className={idx % 2 === 1 ? 'bg-gray-50/50' : ''}>
                        <td className="px-3 py-2 text-gray-700 leading-tight" data-testid={`cat-${cat.key}`}>
                          <span className="text-[10px] font-mono text-gray-400 mr-1.5">{String(idx + 1).padStart(2, '0')}</span>
                          {cat.label}
                        </td>
                        {[1, 2, 3, 4].map((q) => {
                          const snap = grp.quarters[q];
                          const v = snap ? Number(snap.ratings?.[cat.key] || 0) : null;
                          const prevQ = q - 1 >= 1 && grp.quarters[q - 1]
                            ? Number(grp.quarters[q - 1].ratings?.[cat.key] || 0)
                            : null;
                          const d = (v !== null && prevQ !== null) ? v - prevQ : 0;
                          return (
                            <td
                              key={q}
                              className={`text-center px-2 py-2 tabular-nums border-l border-gray-100 ${
                                v === null ? 'text-gray-300' : 'text-gray-900'
                              }`}
                              data-testid={`cell-${cat.key}-fy-${grp.fy}-q${q}`}
                            >
                              {v === null ? (
                                <span className="text-gray-300">—</span>
                              ) : (
                                <span className="inline-flex items-center justify-center gap-1">
                                  <span className="font-medium">{v ? v.toFixed(1) : '0'}</span>
                                  {d !== 0 && prevQ !== null && (
                                    <span className={`inline-flex items-center text-[9px] ${d > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {d > 0 ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                                      {Math.abs(d).toFixed(1)}
                                    </span>
                                  )}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-800 bg-amber-50/60">
                    <tr>
                      <td className="px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-gray-700">Overall Average</td>
                      {[1, 2, 3, 4].map((q) => {
                        const snap = grp.quarters[q];
                        const v = qAvg[q];
                        const prevQ = q - 1 >= 1 ? qAvg[q - 1] : 0;
                        const filled = !!snap;
                        const d = filled && prevQ ? v - prevQ : 0;
                        return (
                          <td key={q} className="text-center px-2 py-2 border-l border-gray-200">
                            {!filled ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              <span className="inline-flex items-center gap-1">
                                <span className="font-heading text-sm font-bold text-amber-800 tabular-nums">
                                  {v.toFixed(1)}
                                </span>
                                {Math.abs(d) >= 0.05 && prevQ > 0 ? (
                                  d > 0
                                    ? <TrendingUp size={11} className="text-green-600" />
                                    : <TrendingDown size={11} className="text-red-600" />
                                ) : prevQ > 0 && filled ? (
                                  <Minus size={11} className="text-gray-400" />
                                ) : null}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    <tr className="border-t border-amber-200/60">
                      <td className="px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-gray-500">Actions</td>
                      {[1, 2, 3, 4].map((q) => {
                        const snap = grp.quarters[q];
                        if (!snap) return <td key={q} className="text-center px-2 py-1.5 border-l border-amber-100/60"></td>;
                        return (
                          <td key={q} className="text-center px-2 py-1.5 border-l border-amber-100/60">
                            <button
                              onClick={() => remove(snap.id)}
                              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-red-600"
                              title={`Delete Q${q} ${grp.fy} snapshot`}
                              data-testid={`delete-snapshot-${snap.id}`}
                            >
                              <Trash2 size={10} /> Delete
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default RatingsTimeline;
